import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, ChevronDown } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { components } from 'react-select';
import ReactSelector from "../common/CommonReactSelector";
import datePickerPopper from "../common/DatePickerPopper";
import useDirection from "../../hooks/useDirection";
import HoverTooltip from "../common/HoverTooltip";

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const formatShortDate = (ymd) => {
  const d = parseYmd(ymd);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const DATE_RANGE_POPOVER_WIDTH = 300;
const DateRangeFilterPopover = ({
  anchorEl,
  onClose,
  fromDate,
  toDate,
  onChangeFrom,
  onChangeTo,
  onClear,
  onApply,
  todayPicker,
  isRtl,
  langMode,
}) => {
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const popoverWidth = Math.min(DATE_RANGE_POPOVER_WIDTH, window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - popoverWidth - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left, width: popoverWidth });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      // The calendar popups are portaled to document.body via datePickerPopper,
      // so they're siblings of this popover in the DOM, not descendants -- a
      // click inside one would otherwise look like an "outside" click.
      if (event.target.closest?.(".react-datepicker, .datepicker-portal")) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width: popoverStyle.width || DATE_RANGE_POPOVER_WIDTH }}
      className="z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal"
      onClick={(e) => e.stopPropagation()}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].fromdate}</label>
          <DatePicker
            selected={parseYmd(fromDate)}
            onChange={onChangeFrom}
            placeholderText={translations[langMode].fromdate}
            maxDate={todayPicker}
            selectsStart
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">{translations[langMode].todate}</label>
          <DatePicker
            selected={parseYmd(toDate)}
            onChange={onChangeTo}
            placeholderText={translations[langMode].todate}
            minDate={parseYmd(fromDate)}
            maxDate={todayPicker}
            selectsEnd
            startDate={parseYmd(fromDate)}
            endDate={parseYmd(toDate)}
            disabled={!fromDate}
            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
            calendarClassName="!rounded-lg shadow-lg"
            popperPlacement="bottom-start"
            popperContainer={datePickerPopper}
          />
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
        <button type="button" onClick={onClear} className="text-xs font-medium text-slate-500 hover:underline">
          {translations[langMode].reset || "Clear"}
        </button>
        <button data-button-intent="primary"
          type="button"
          onClick={onApply}
          className="h-8 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96]"
        >
          {translations[langMode].filter || "Apply"}
        </button>
      </div>
    </div>,
    document.body
  );
};

const ReportToolbar = ({ onFiltersChange }) => {
  const [ReportType, setReportType] = useState("");
  const [UserType, setUserType] = useState("");
  const [Status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const { langMode } = useContext(AppContext);
  const { isRTL: isRtl } = useDirection();
  const [showDateRangePopover, setShowDateRangePopover] = useState(false);
  const dateRangeButtonRef = useRef(null);
  const hasDateRangeFilter = Boolean(fromDate || toDate);
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const reportType = [
    { id: "", value: "Default" },
    { id: "VEHICLES", value: "Vehicles" },
    { id: "INVENTORY", value: "Inventory" },
  ];

  const reportTypeOption = reportType.map((report) => ({
    value: report.id,
    label: report.value,
  }));
  const userType = [
    { id: "", value: "Default" },
    { id: "admin", value: "Admin" },
    { id: "office_user", value: "Office_user" },
  ];
  const UserTypeOption = userType.map((user) => ({
    value: user.id,
    label: user.value,
  }));

  const status = [
    { id: "", value: "Default" },
    { id: "0", value: "Pending" },
    { id: "1", value: "Ready To Download" },
  ];

  const statusOption = status.map((st) => ({
    value: st.id,
    label: st.value,
  }));

  useEffect(() => {
    onFiltersChange({
      ReportType,
      UserType,
      Status,
      fromDate: fromDate ? formatYmd(parseYmd(fromDate)) : "",
      toDate: toDate ? formatYmd(parseYmd(toDate)) : "",
    });
  }, [ReportType, UserType, Status, fromDate, toDate, onFiltersChange]);
  const handleReportType = (value) => {
    if (value === "Default") {
      setReportType("");
    } else {
      setReportType(value.value);
    }
  };

  const handleUserTypeChange = (value) => {
    if (value === "Default") {
      setUserType("");
    } else {
      setUserType(value.value);
    }
  };
  
  const selectedStatusOption = statusOption.find(
    (option) => option.value === Status
  );

  const handleStatusChange = (selectedOption) => {
    if (selectedOption.value === "Default") {
      setStatus("");
    }
    if (selectedOption) {
      setStatus(selectedOption.value);
    } else {
      setStatus("");
    }
  };

  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 w-full min-w-0">
      <HoverTooltip
        text={translations[langMode]?.reportTypeTooltip ?? "Filter reports by report type"}
        className="relative block col-span-1 min-w-0 sm:w-44"
      >
        <ReactSelector
          defaultValue={translations[langMode].reportType}
          options={reportTypeOption}
          components={Placeholder}
          placeholder={translations[langMode].reportType}
          onChange={handleReportType}
          height="36px"
          menuPortalTarget={document.body}
        />
      </HoverTooltip>
      <HoverTooltip
        text={translations[langMode]?.userTypeTooltip ?? "Filter reports by user type"}
        className="relative block col-span-1 min-w-0 sm:w-44"
      >
        <ReactSelector
          defaultValue={translations[langMode].userType}
          options={UserTypeOption}
          components={Placeholder}
          placeholder={translations[langMode].userType}
          onChange={handleUserTypeChange}
          height="36px"
          menuPortalTarget={document.body}
        />
      </HoverTooltip>
      <HoverTooltip
        text={translations[langMode]?.statusTooltip ?? "Filter reports by status"}
        className="relative block col-span-1 min-w-0 sm:w-44"
      >
        <ReactSelector
          options={statusOption}
          defaultValue={translations[langMode].status}
          components={Placeholder}
          placeholder={translations[langMode].status}
          onChange={handleStatusChange} // Use the new handler
          height="36px"
          menuPortalTarget={document.body}
        />
      </HoverTooltip>

      <div ref={dateRangeButtonRef} className="relative h-9 col-span-1 min-w-0 w-full sm:w-auto">
        <HoverTooltip
          text={translations[langMode]?.dateRangeTooltip ?? "Filter reports by date range"}
          className="relative inline-flex w-full sm:w-auto"
        >
          <button
            type="button"
            onClick={() => setShowDateRangePopover((v) => !v)}
            className="h-9 w-full sm:w-auto px-2.5 sm:px-3 rounded-lg border text-xs sm:text-sm font-medium flex items-center justify-between sm:justify-center gap-1 sm:gap-2 bg-white whitespace-nowrap"
            style={{
              borderColor: showDateRangePopover || hasDateRangeFilter ? "#93C5FD" : "#E2E8F0",
              background: showDateRangePopover ? "#EFF6FF" : "white",
              color: showDateRangePopover || hasDateRangeFilter ? "#1D4ED8" : "#334155",
            }}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <Calendar size={14} className="shrink-0" />
              <span className="truncate">
                {hasDateRangeFilter
                  ? `${formatShortDate(fromDate) || "…"} – ${formatShortDate(toDate) || "…"}`
                  : translations[langMode]?.dateRange ?? "Date Range"}
              </span>
            </div>
            <ChevronDown size={14} className={`shrink-0 text-slate-400 ${showDateRangePopover ? "rotate-180" : ""}`} />
          </button>
        </HoverTooltip>
        {showDateRangePopover && (
          <DateRangeFilterPopover
            anchorEl={dateRangeButtonRef.current}
            onClose={() => setShowDateRangePopover(false)}
            fromDate={fromDate}
            toDate={toDate}
            onChangeFrom={(date) => {
              setFromDate(date ? formatYmd(date) : "");
              if (date && parseYmd(toDate) && parseYmd(toDate) < date) {
                setToDate(formatYmd(date));
              }
            }}
            onChangeTo={(date) => setToDate(date ? formatYmd(date) : "")}
            onClear={() => {
              setFromDate("");
              setToDate("");
              setShowDateRangePopover(false);
            }}
            onApply={() => setShowDateRangePopover(false)}
            todayPicker={todayPicker}
            isRtl={isRtl}
            langMode={langMode}
          />
        )}
      </div>
    </div>
  );
};

export default ReportToolbar;
